/**
 * Единый транспорт изображений PIM.
 *
 * Браузер не отдаёт заголовок Authorization при загрузке `<img src>` — токен
 * лежит в localStorage, а не в cookie. Поэтому любой закрытый маршрут гейтвея,
 * вставленный в разметку прямой ссылкой, отвечает 401 и на экране остаётся
 * серая заглушка. Здесь картинка забирается обычным авторизованным запросом
 * (тот же axios-клиент, что и весь остальной API), а в `<img>` подставляется
 * object URL полученного blob.
 *
 * Две природы адресов, и правило между ними жёсткое:
 *  - путь начинается с `/api/` на origin страницы или гейтвея — наш маршрут,
 *    идёт через авторизованную загрузку;
 *  - всё остальное (абсолютный адрес внешнего хоста, `data:`, `blob:`) —
 *    прямая ссылка без изменений. Публичные адреса визуалов воронок
 *    (`FTP_PUBLIC_BASE`, по ним WB и OZON скачивают картинки при создании
 *    карточки) обязаны оставаться публичными, заворачивать их нельзя.
 */

import React, { forwardRef, useEffect, useState, type ImgHTMLAttributes, type ReactNode } from 'react'
import { ImageOff } from 'lucide-react'
import { api } from '../api/client'
import { cn } from '../../lib/cn'
import { useNearViewport } from './useNearViewport'

function pageOrigin(): string {
  return globalThis.location?.origin || 'http://localhost'
}

/** Разобранный `baseURL` axios: из него берём и origin гейтвея, и префикс пути. */
function apiBase(): URL {
  const raw = String(api.defaults.baseURL || '/api/v1')
  try {
    return new URL(raw, pageOrigin())
  } catch {
    return new URL('/api/v1', pageOrigin())
  }
}

/**
 * Адрес ведёт на наш гейтвей? Проверяем именно origin + префикс `/api/`, а не
 * имя хоста: публичный префикс визуалов задаётся настройкой и в разных средах
 * разный, полагаться на него нельзя.
 */
export function isInternalApiMediaUrl(rawUrl?: string | null): boolean {
  if (!rawUrl) return false
  try {
    const origin = pageOrigin()
    const parsed = new URL(rawUrl, origin)
    if (parsed.origin !== origin && parsed.origin !== apiBase().origin) return false
    return parsed.pathname.startsWith('/api/')
  } catch {
    return false
  }
}

/**
 * Путь для axios-клиента. В разметке лежит полный путь гейтвея
 * (`/api/v1/pipeline/visual?...`), а у клиента уже задан `baseURL=/api/v1` —
 * без снятия префикса запрос ушёл бы на `/api/v1/api/v1/...` и вернул 404.
 */
export function toApiClientPath(rawUrl: string): string {
  const parsed = new URL(rawUrl, pageOrigin())
  const basePath = apiBase().pathname.replace(/\/+$/, '')
  const path = parsed.pathname
  if (basePath && (path === basePath || path.startsWith(`${basePath}/`))) {
    return `${path.slice(basePath.length) || '/'}${parsed.search}`
  }
  return `${path}${parsed.search}`
}

/**
 * Добавляет ширину серверной миниатюры к закрытому media-адресу.
 *
 * Внешние публичные картинки оставляем без изменений: параметр `w` понимают
 * только наши маршруты, а произвольному CDN он может изменить смысл ссылки.
 * Повторный вызов заменяет `w`, сохраняя остальные параметры (`path`,
 * `preview` и т. п.). Без ширины возвращается исходный адрес — это важный
 * контракт для лайтбоксов и скачивания оригиналов.
 */
export function thumbnailMediaUrl(source: string, width?: number): string {
  if (!width || !Number.isFinite(width) || !isInternalApiMediaUrl(source)) return source

  const parsed = new URL(source, pageOrigin())
  parsed.searchParams.set('w', String(Math.round(width)))

  // Не превращаем относительный API-путь в абсолютный: axios-клиент сам знает
  // origin гейтвея, а тестовые и локальные окружения могут работать на другом.
  if (source.startsWith('/')) {
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  }
  return parsed.toString()
}

/* ── Общий кэш загруженных blob'ов ───────────────────────────────────────── */

interface MediaCacheEntry {
  /** Сколько компонентов сейчас показывают этот адрес */
  refs: number
  objectUrl?: string
  /** Размер полученного blob'а — по нему считается бюджет хвоста */
  bytes: number
  disposed: boolean
  controller: AbortController
  promise: Promise<string>
}

const mediaCache = new Map<string, MediaCacheEntry>()

/* ── Потолок одновременных запросов ──────────────────────────────────────── */

/**
 * Сколько картинок едет по сети одновременно.
 *
 * Каталог рисует до сотни строк разом, и без потолка столько же XHR уходит в
 * одну секунду. Прямая ссылка в `<img>` так себя не вела: браузер сам держал
 * очередь и не трогал картинки за пределами экрана (`loading="lazy"`). Перед
 * боевым стендом стоит лимитер, и лишние запросы он рубит, не доводя до
 * приложения: 09.08.2026 пачка из 40 запросов к pim.printort.ru вернула 9
 * ответов «503 Service Temporarily Unavailable» от nginx и 4 обрыва
 * соединения. Шесть — привычный для браузера предел на один хост.
 */
export const MEDIA_REQUEST_CONCURRENCY = 6

/**
 * Потолок параллельности сам по себе темп не держит: он ограничивает, сколько
 * запросов висит одновременно, а не сколько уходит в секунду. Пока превью
 * весило под мегабайт, шесть медленных запросов давали около одного в секунду,
 * и лимитер мы не задевали. С миниатюрами ответ похудел в девяносто раз и
 * приходит за 139 мс — те же шесть слотов обернулись 41 запросом в секунду, и
 * 11.08.2026 каталог на проде получил 58 отказов «503» из 100.
 *
 * Лимитер перед стендом пропускает пачку около двадцати, дальше 8–10 в секунду.
 * Этот бюджет общий на весь трафик вкладки, и картинкам нельзя занимать его
 * целиком: 11.08.2026 при темпе 7 в секунду отказ по 503 получил уже сам список
 * макетов. С бесконечной прокруткой это штатная ситуация — очередная порция
 * запрашивается ровно тогда, когда ещё доливаются превью предыдущей.
 *
 * Темп 4 в секунду был выбран под ту картину и стоил дорого: 60 плиток порции
 * каталога физически не могли прийти быстрее 15 секунд, хотя замер 19.08.2026
 * показал, что сервер отдаёт всю порцию за 0,33 с при шести параллельных.
 * Ограничением был не бэкенд, а этот потолок.
 *
 * Поэтому темп поднят до 16 в секунду, но не жёстко: запрошенный темп —
 * верхняя граница, с которой начинаем. Отказ лимитера (429 от гейтвея, 503 от
 * nginx) вдвое опускает текущий темп, каждый успешный ответ поднимает его на
 * четверть запроса в секунду обратно к потолку. Так вкладка сама нащупывает
 * то, что стенд готов пропустить, вместо зашитого числа: пока зоны limit_req
 * под media на боевом nginx нет, темп осядет там, где перестанут приходить
 * отказы, а не устроит повтор 11.08.2026.
 *
 * Ниже MEDIA_REQUEST_MIN_RATE_PER_SECOND не опускаемся — это прежние 4 в
 * секунду, на которых каталог заведомо работал.
 */
export const MEDIA_REQUEST_BURST = 16
export const MEDIA_REQUEST_RATE_PER_SECOND = 16
export const MEDIA_REQUEST_MIN_RATE_PER_SECOND = 4

let ratePerSecond = MEDIA_REQUEST_RATE_PER_SECOND
let rateTokens = MEDIA_REQUEST_BURST
let rateRefilledAt = Date.now()

/** Текущий темп: он плавает между минимумом и потолком. Нужен и тестам. */
export function mediaRequestRatePerSecond(): number {
  return ratePerSecond
}

/**
 * Лимитер отказал — темп вдвое вниз. Половинное деление гасит наплыв за
 * считанные отказы, а не за десятки: с 16 до пола в 4 хватает двух.
 */
function noteLimiterRefusal(): void {
  ratePerSecond = Math.max(MEDIA_REQUEST_MIN_RATE_PER_SECOND, ratePerSecond / 2)
}

/**
 * Ответ дошёл — осторожно возвращаемся к потолку. Прибавка мелкая намеренно:
 * подниматься надо медленнее, чем падать, иначе темп заколеблется вокруг
 * границы лимитера, снимая по отказу на каждом цикле.
 */
function noteLimiterAccepted(): void {
  ratePerSecond = Math.min(MEDIA_REQUEST_RATE_PER_SECOND, ratePerSecond + 0.25)
}

/** Сколько миллисекунд ждать до следующего запроса; 0 — можно прямо сейчас. */
function reserveRateSlot(): number {
  const now = Date.now()
  // Часы могут уйти назад — перевод времени, синхронизация NTP, фейковые
  // таймеры в тестах. Без нижней границы ведро провалилось бы в минус и
  // картинки перестали бы грузиться до конца сессии.
  const elapsedMs = Math.max(0, now - rateRefilledAt)
  rateTokens = Math.min(
    MEDIA_REQUEST_BURST,
    rateTokens + (elapsedMs / 1000) * ratePerSecond,
  )
  rateRefilledAt = now
  if (rateTokens >= 1) {
    rateTokens -= 1
    return 0
  }
  const waitMs = ((1 - rateTokens) / ratePerSecond) * 1000
  rateTokens -= 1
  return Math.ceil(waitMs)
}

let activeRequests = 0
const queuedRequests: Array<() => void> = []

/** Освобождает слот и запускает следующего из очереди. */
function releaseRequestSlot(): void {
  activeRequests = Math.max(0, activeRequests - 1)
  const next = queuedRequests.shift()
  if (next) next()
}

/**
 * Ставит загрузку в очередь: одновременно в сети не более
 * MEDIA_REQUEST_CONCURRENCY запросов и не быстрее разрешённого темпа.
 * Дождавшийся своей очереди потребитель может быть уже не нужен — тогда в сеть
 * не идём вовсе (список прокрутили).
 */
function enqueueMediaRequest<T>(stillNeeded: () => boolean, task: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => {
      if (!stillNeeded()) {
        reject(new Error('Загрузка изображения отменена'))
        releaseRequestSlot()
        return
      }
      const waitMs = reserveRateSlot()
      const start = () => {
        // Пока ждали своей доли темпа, строка могла уехать с экрана.
        if (!stillNeeded()) {
          reject(new Error('Загрузка изображения отменена'))
          releaseRequestSlot()
          return
        }
        task().then(resolve, reject).finally(releaseRequestSlot)
      }
      if (waitMs > 0) setTimeout(start, waitMs)
      else start()
    }

    if (activeRequests < MEDIA_REQUEST_CONCURRENCY) {
      activeRequests += 1
      run()
    } else {
      queuedRequests.push(() => {
        activeRequests += 1
        run()
      })
    }
  })
}

/**
 * Ключи без активных потребителей. Хвост нужен, чтобы перемонтирование плитки
 * (прокрутка, StrictMode, отложенная загрузка по видимости) не било по сети
 * заново, но и накапливать всё подряд нельзя.
 *
 * Бюджет считаем в байтах, а не в записях: миниатюра каталога весит 5–10 КБ,
 * а оригинал в карточке макета — единицы мегабайт. Общий счётчик на 24 записи
 * означал либо жалкие 24 плитки каталога (прокрутил один экран назад — снова
 * сеть), либо десятки мегабайт, если в хвост попали оригиналы. Потолок по
 * числу записей оставлен страховкой от вырождения на очень мелких картинках.
 */
const idleKeys: string[] = []
const IDLE_BYTES_LIMIT = 24 * 1024 * 1024
const IDLE_ENTRY_LIMIT = 512

function forgetIdle(key: string): void {
  const at = idleKeys.indexOf(key)
  if (at >= 0) idleKeys.splice(at, 1)
}

function dropEntry(key: string): void {
  const entry = mediaCache.get(key)
  if (!entry) return
  mediaCache.delete(key)
  forgetIdle(key)
  entry.disposed = true
  entry.controller.abort()
  if (entry.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl)
    entry.objectUrl = undefined
  }
}

function idleBytes(): number {
  let total = 0
  for (const key of idleKeys) total += mediaCache.get(key)?.bytes || 0
  return total
}

function trimIdle(): void {
  while (idleKeys.length > IDLE_ENTRY_LIMIT || (idleKeys.length > 0 && idleBytes() > IDLE_BYTES_LIMIT)) {
    const key = idleKeys.shift()
    if (key) dropEntry(key)
  }
}

/**
 * Ответы лимитера: nginx на отказ по темпу отдаёт 503, гейтвей на свой лимит —
 * 429. И то и другое значит «приходи позже», а не «файла нет», поэтому плитку
 * гасить рано.
 *
 * 502 и 504 сюда же: перезапуск сервиса при выкладке и таймаут долгой отрисовки
 * тоже лечатся повтором, а на экране до этой правки давали погасшую плитку до
 * перезагрузки страницы. Настоящее отсутствие файла приходит как 404 и
 * повторов по-прежнему не получает.
 */
const RETRIABLE_STATUSES = new Set([429, 502, 503, 504])
const RETRY_DELAYS_MS = [400, 1200, 2600]

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | undefined)?.response?.status
}

/**
 * Тянет байты, переспрашивая при отказе по темпу. Разброс в паузе разводит
 * запросы, синхронно получившие отказ: без него они вернутся тем же залпом.
 */
async function fetchMediaBlob(source: string, signal: AbortSignal) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await api.get<Blob>(toApiClientPath(source), { responseType: 'blob', signal })
      noteLimiterAccepted()
      return response
    } catch (error) {
      const status = statusOf(error)
      if (status !== undefined && RETRIABLE_STATUSES.has(status)) noteLimiterRefusal()
      if (attempt >= RETRY_DELAYS_MS.length || status === undefined || !RETRIABLE_STATUSES.has(status)) {
        throw error
      }
      const pause = RETRY_DELAYS_MS[attempt] + Math.random() * 250
      await new Promise<void>((resolve) => setTimeout(resolve, pause))
      if (signal.aborted) throw error
    }
  }
}

/** Берёт (и при необходимости начинает) загрузку адреса, увеличивая счётчик. */
export function acquireAuthorizedMedia(source: string): MediaCacheEntry {
  const existing = mediaCache.get(source)
  if (existing) {
    forgetIdle(source)
    existing.refs += 1
    return existing
  }

  const controller = new AbortController()
  const entry: MediaCacheEntry = {
    refs: 1,
    bytes: 0,
    disposed: false,
    controller,
    promise: undefined as unknown as Promise<string>,
  }
  entry.promise = enqueueMediaRequest(
    () => !entry.disposed,
    () => fetchMediaBlob(source, controller.signal),
  )
    .then((response) => {
      const objectUrl = URL.createObjectURL(response.data)
      if (entry.disposed) {
        URL.revokeObjectURL(objectUrl)
        throw new Error('Загрузка изображения отменена')
      }
      entry.objectUrl = objectUrl
      entry.bytes = Number(response.data?.size) || 0
      return objectUrl
    })
    .catch((reason: unknown) => {
      // Ошибку не кэшируем: следующий показ должен сходить в сеть заново.
      if (mediaCache.get(source) === entry) {
        mediaCache.delete(source)
        forgetIdle(source)
      }
      throw reason instanceof Error ? reason : new Error('Не удалось загрузить изображение')
    })
  // Гасим unhandled rejection, если потребитель уже ушёл с экрана.
  entry.promise.catch(() => undefined)

  mediaCache.set(source, entry)
  return entry
}

/** Отпускает адрес; последний ушедший потребитель отправляет blob в хвост. */
export function releaseAuthorizedMedia(source: string): void {
  const entry = mediaCache.get(source)
  if (!entry) return
  entry.refs = Math.max(0, entry.refs - 1)
  if (entry.refs > 0) return
  if (!idleKeys.includes(source)) idleKeys.push(source)
  trimIdle()
}

/** Только для тестов: полностью очищает кэш и отзывает object URL. */
export function resetAuthorizedMediaCache(): void {
  for (const key of Array.from(mediaCache.keys())) dropEntry(key)
  idleKeys.length = 0
  queuedRequests.length = 0
  activeRequests = 0
  ratePerSecond = MEDIA_REQUEST_RATE_PER_SECOND
  rateTokens = MEDIA_REQUEST_BURST
  rateRefilledAt = Date.now()
}

/* ── Хук ─────────────────────────────────────────────────────────────────── */

export interface AuthorizedMediaState {
  src?: string
  loading: boolean
  error: Error | null
}

/**
 * Адреса нашего гейтвея превращаются в object URL, остальные возвращаются как
 * есть — внешние CDN и публичные визуалы воронок через API не ходят.
 */
export function useAuthorizedMediaUrl(source?: string): AuthorizedMediaState {
  const internalSource = Boolean(source && isInternalApiMediaUrl(source))
  const [state, setState] = useState<AuthorizedMediaState & { source?: string }>(() => ({
    source,
    src: internalSource ? undefined : source,
    loading: internalSource,
    error: null,
  }))

  useEffect(() => {
    if (!source || !isInternalApiMediaUrl(source)) {
      setState({ source, src: source, loading: false, error: null })
      return
    }

    let active = true
    const entry = acquireAuthorizedMedia(source)
    // Готовый blob подставляем сразу — иначе список мигал бы скелетом на каждой
    // перерисовке, хотя картинка уже в памяти.
    setState(
      entry.objectUrl
        ? { source, src: entry.objectUrl, loading: false, error: null }
        : { source, src: undefined, loading: true, error: null },
    )

    entry.promise.then(
      (objectUrl) => {
        if (active) setState({ source, src: objectUrl, loading: false, error: null })
      },
      (reason: unknown) => {
        if (!active) return
        setState({
          source,
          src: undefined,
          loading: false,
          error: reason instanceof Error ? reason : new Error('Не удалось загрузить изображение'),
        })
      },
    )

    return () => {
      active = false
      releaseAuthorizedMedia(source)
    }
  }, [source])

  return state.source === source
    ? state
    : { src: internalSource ? undefined : source, loading: internalSource, error: null }
}

/* ── Состояния показа ────────────────────────────────────────────────────── */

export const MEDIA_ERROR_TITLE = 'Не удалось загрузить изображение'

/**
 * Скелет загрузки: заметно, что картинка ещё едет, а не что её нет.
 *
 * `forwardRef`, потому что тот же узел служит наблюдаемым элементом для
 * отложенной загрузки по видимости (см. `useNearViewport`) — до появления в
 * области видимости других узлов, кроме этого скелета, ещё нет.
 */
export const MediaLoadingBox = forwardRef<HTMLSpanElement, { className?: string; label?: string }>(
  function MediaLoadingBox({ className, label = 'Загрузка изображения' }, ref) {
    return (
      <span
        ref={ref}
        role="status"
        aria-label={label}
        className={cn('inline-block animate-pulse bg-gray-100 dark:bg-zinc-800', className)}
      />
    )
  },
)

/**
 * Ошибка загрузки. Отличается от «фото нет» рамкой и значком: пустая серая
 * плитка выглядела бы как отсутствие изображения, а причина здесь другая.
 */
export function MediaErrorBox({
  className,
  label = MEDIA_ERROR_TITLE,
  iconClassName = 'h-4 w-4',
}: {
  className?: string
  label?: string
  iconClassName?: string
}) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center border border-dashed border-red-300 bg-red-50 text-red-400',
        'dark:border-red-900 dark:bg-red-950/30 dark:text-red-500',
        className,
      )}
    >
      <ImageOff className={iconClassName} aria-hidden="true" />
    </span>
  )
}

interface AuthorizedMediaImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string
  /** Ширина серверной WebP-миниатюры; без значения загружается оригинал. */
  thumbnailWidth?: number
  /**
   * Запасной адрес кадра.
   *
   * Нужен там, где кадр есть в двух местах и предпочтительное дешевле, но не
   * гарантировано: визуал воронки лежит и в нашем томе (маршрут умеет
   * миниатюру), и на публичном хосте (полный кадр в полмегабайта). Просим
   * первое, на промахе честно показываем второе — вместо погашенной плитки.
   * Тот же приём у `ImagePreviewThumb`.
   */
  fallbackSrc?: string
  /**
   * Грузить, только когда узел подъехал к экрану. По умолчанию включено — тем
   * же хуком `useNearViewport`, что и `ImagePreviewThumb`, — потому что у этого
   * компонента отдельного механизма отложенной загрузки не было вовсе: байты
   * тянулись авторизованным запросом на монтировании независимо от видимости.
   * Невиртуализированные списки (черновики ПИМ, приёмка макетов в режиме
   * «Фото») стреляли сотнями запросов разом.
   *
   * Выключать (`lazy={false}`) стоит там, где картинка одна и заведомо в кадре
   * сразу при открытии: одиночное крупное превью, центральный кадр модалки,
   * лайтбокс.
   */
  lazy?: boolean
  loadingFallback?: ReactNode
  errorFallback?: ReactNode
}

/** `<img>` с bearer-транспортом, явными loading/error-состояниями и отложенной загрузкой по видимости. */
export const AuthorizedMediaImage = forwardRef<HTMLImageElement, AuthorizedMediaImageProps>(
  function AuthorizedMediaImage(
    { src: source, thumbnailWidth, alt = '', fallbackSrc, lazy = true, loadingFallback, errorFallback, ...imageProps },
    ref,
  ) {
    const [fellBack, setFellBack] = useState(false)
    // Смена адреса начинает историю заново: прежний промах к нему не относится.
    useEffect(() => setFellBack(false), [source, fallbackSrc])
    const { ref: viewportRef, near } = useNearViewport()
    const visible = !lazy || near
    const media = useAuthorizedMediaUrl(
      visible
        ? thumbnailMediaUrl(fellBack && fallbackSrc ? fallbackSrc : source, thumbnailWidth)
        : undefined,
    )
    useEffect(() => {
      if (fallbackSrc && !fellBack && media.error) setFellBack(true)
    }, [fallbackSrc, fellBack, media.error])

    // Пока узел далеко от экрана — держим место скелетом: он же и есть
    // наблюдаемый элемент, запрос в сеть ещё не начат.
    if (!visible) {
      return loadingFallback === undefined ? (
        <MediaLoadingBox ref={viewportRef} className={imageProps.className} />
      ) : (
        <span ref={viewportRef}>{loadingFallback}</span>
      )
    }

    if (media.loading) {
      return loadingFallback === undefined ? (
        <MediaLoadingBox className={imageProps.className} />
      ) : (
        <>{loadingFallback}</>
      )
    }
    if (media.error || !media.src) {
      return errorFallback === undefined ? (
        <MediaErrorBox
          className={imageProps.className}
          label={alt ? `${alt}: ${MEDIA_ERROR_TITLE.toLowerCase()}` : MEDIA_ERROR_TITLE}
        />
      ) : (
        <>{errorFallback}</>
      )
    }

    return <img ref={ref} src={media.src} alt={alt} {...imageProps} />
  },
)

/* ── Скачивание и открытие файлов ────────────────────────────────────────── */

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
}

/**
 * Байты файла нашего гейтвея — для потребителей, которым нужен не `<img>`, а
 * сам буфер (pdf.js рендерит первую страницу макета в canvas).
 */
export async function fetchAuthorizedMediaBuffer(source: string): Promise<ArrayBuffer> {
  if (!isInternalApiMediaUrl(source)) {
    throw new Error('Недопустимый URL защищённого медиа')
  }
  const response = await api.get<ArrayBuffer>(toApiClientPath(source), {
    responseType: 'arraybuffer',
  })
  return response.data
}

export async function downloadAuthorizedMedia(source: string, fileName: string): Promise<void> {
  if (!isInternalApiMediaUrl(source)) {
    throw new Error('Недопустимый URL защищённого медиа')
  }
  const response = await api.get<Blob>(toApiClientPath(source), { responseType: 'blob' })
  triggerBlobDownload(response.data, fileName)
}

/** Открывает PDF/файл в новой вкладке, не раскрывая защищённый URL браузеру без auth. */
export async function openAuthorizedMedia(source: string): Promise<void> {
  if (!isInternalApiMediaUrl(source)) {
    // Внешние публичные адреса открываются как есть — заворачивать их нельзя.
    const external = window.open(source, '_blank', 'noopener')
    if (!external) throw new Error('Браузер заблокировал окно просмотра')
    return
  }
  const preview = window.open('', '_blank')
  if (!preview) throw new Error('Браузер заблокировал окно просмотра')
  preview.opener = null
  try {
    const response = await api.get<Blob>(toApiClientPath(source), { responseType: 'blob' })
    const objectUrl = URL.createObjectURL(response.data)
    preview.location.replace(objectUrl)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  } catch (error) {
    preview.close()
    throw error
  }
}
